import React, {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type FC,
  useCallback,
} from "react";
import CustomCloseIcon from "../../../../assets/icons/CustomCloseIcon";
import type { LiveStatusModalProps } from "../../../../type/components";
import BetPreview from "./BetPreview/BetPreview";
import "./liveStatus.css";

const LiveStatusModal: FC<LiveStatusModalProps> = ({ setOpenModal }) => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef({ x: 0, y: 0 }); // ✅ useRef instead of useState
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 850, y: 150 });

  useEffect(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    if (!divRef.current) return;

    const modalRect = divRef.current.getBoundingClientRect();

    if (width <= 1200 && width > 999) {
      setPosition({
        x: 600,
        y: height / 2 - modalRect.height / 2,
      });
    } else if (width <= 999 && width > 576) {
      setPosition({
        x: 100,
        y: height / 2 - modalRect.height / 2,
      });
    } else if (width <= 576) {
      setPosition({
        x: 20,
        y: height / 2 - modalRect.height / 2,
      });
    }
  }, []);

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;

    setIsDragging(true);
    const rect = divRef.current.getBoundingClientRect();

    offsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    document.body.style.userSelect = "none";
  };

const handleMouseMove = useCallback(
  (e: MouseEvent | globalThis.MouseEvent) => {
    if (!isDragging) return;

    // Get the window width and height
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Get the modal's width and height
    if (!divRef.current) return;
    const modalRect = divRef.current.getBoundingClientRect();

    // Calculate the new position
    let newX = e.clientX - offsetRef.current.x;
    let newY = e.clientY - offsetRef.current.y;

    // Constrain the modal to stay within the bounds of the window
    if (newX < 0) newX = 0; // Prevent going left
    if (newY < 0) newY = 0; // Prevent going above the window

    if (newX + modalRect.width > windowWidth) {
      newX = windowWidth - modalRect.width; // Prevent going right
    }

    if (newY + modalRect.height > windowHeight) {
      newY = windowHeight - modalRect.height; // Prevent going below the window
    }

    setPosition({ x: newX, y: newY });
  },
  [isDragging]
);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.userSelect = "auto";
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={divRef}
      onMouseDown={handleMouseDown}
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        zIndex: 1000,
      }}
    >
      <div className="live-status-modal">
        {/* Header */}
        <div className="live-status-header">
          <span className="live-status-title">Live Stats</span>
          <div
            className="live-status-close"
            onClick={() => setOpenModal(false)}
          >
            <CustomCloseIcon />
          </div>
        </div>

        {/* Content */}
        <div className="live-status-content">
          <BetPreview />
        </div>
      </div>
    </div>
  );
};

export default LiveStatusModal;